/**
 * Zahlen für den Kostenvergleich auf /preise.
 * Alle Agentur-Werte sind marktübliche Richtwerte (Spannen), keine Angebote.
 * Der Coin-Preis kommt weiterhin aus salonturbo.ts – hier keine zweite Wahrheit.
 */
import { COIN_PRICE_CENTS, DAILY_BUDGET_EUR } from "@/lib/salonturbo";

/** Kalkulatorischer Stundensatz für die Zeit des Inhabers. */
export const OWNER_HOURLY_RATE_EUR = 45;

/** Referenz-Szenario für die Rechnung: geboostete Tage pro Monat. */
export const SCENARIO_BOOST_DAYS = 4;

export const agencyRanges = {
  setupMin: 300,
  setupMax: 800,
  retainerMin: 150,
  retainerMax: 400,
  landingMin: 500,
  landingMax: 2000,
  ownerHoursMin: 2,
  ownerHoursMax: 4,
} as const;

const mid = (min: number, max: number) => Math.round((min + max) / 2);

export const agencyMid = {
  setup: mid(agencyRanges.setupMin, agencyRanges.setupMax),
  retainer: mid(agencyRanges.retainerMin, agencyRanges.retainerMax),
  landing: mid(agencyRanges.landingMin, agencyRanges.landingMax),
  ownerHours: mid(agencyRanges.ownerHoursMin, agencyRanges.ownerHoursMax),
} as const;

export type CostRow = {
  label: string;
  agency: string;
  agencyEur: number;
  salonturbo: string;
  salonturboEur: number;
  note?: string;
};

const oneOffPerMonth = Math.round((agencyMid.setup + agencyMid.landing) / 12);
const agencyOwnerTimeEur = agencyMid.ownerHours * OWNER_HOURLY_RATE_EUR;
const agencyBudgetEur = SCENARIO_BOOST_DAYS * DAILY_BUDGET_EUR;
/** ca. 2 Minuten pro Woche = ~9 Minuten im Monat */
const turboOwnerMinutes = 9;
const turboOwnerTimeEur = Math.round((turboOwnerMinutes / 60) * OWNER_HOURLY_RATE_EUR);
const turboCoinsEur = (SCENARIO_BOOST_DAYS * COIN_PRICE_CENTS) / 100;

export const costRows: CostRow[] = [
  {
    label: "Setup Konto & Kampagne",
    agency: `${agencyRanges.setupMin}–${agencyRanges.setupMax} € einmalig`,
    agencyEur: 0,
    salonturbo: "inklusive",
    salonturboEur: 0,
    note: "einmalig, unten auf 12 Monate verteilt",
  },
  {
    label: "Landingpage für den Salon",
    agency: `${agencyRanges.landingMin}–${agencyRanges.landingMax} € einmalig`,
    agencyEur: 0,
    salonturbo: "inklusive",
    salonturboEur: 0,
    note: "einmalig, unten auf 12 Monate verteilt",
  },
  {
    label: "Anteil Einmalkosten pro Monat",
    agency: `ca. ${oneOffPerMonth} €`,
    agencyEur: oneOffPerMonth,
    salonturbo: "0 €",
    salonturboEur: 0,
  },
  {
    label: "Monatliche Betreuung",
    agency: `${agencyRanges.retainerMin}–${agencyRanges.retainerMax} €`,
    agencyEur: agencyMid.retainer,
    salonturbo: "0 €",
    salonturboEur: 0,
  },
  {
    label: `Werbebudget (${SCENARIO_BOOST_DAYS} Tage)`,
    agency: `ca. ${agencyBudgetEur} € zusätzlich`,
    agencyEur: agencyBudgetEur,
    salonturbo: "im Coin enthalten",
    salonturboEur: 0,
  },
  {
    label: `${SCENARIO_BOOST_DAYS} Boost-Tage`,
    agency: "–",
    agencyEur: 0,
    salonturbo: `${SCENARIO_BOOST_DAYS} Coins = ${turboCoinsEur} €`,
    salonturboEur: turboCoinsEur,
  },
  {
    label: "Deine Zeit",
    agency: `${agencyRanges.ownerHoursMin}–${agencyRanges.ownerHoursMax} h/Monat`,
    agencyEur: agencyOwnerTimeEur,
    salonturbo: "ca. 2 Min/Woche",
    salonturboEur: turboOwnerTimeEur,
    note: `bewertet mit ${OWNER_HOURLY_RATE_EUR} €/Stunde`,
  },
];

export const costTotals = {
  agency: oneOffPerMonth + agencyMid.retainer + agencyBudgetEur + agencyOwnerTimeEur,
  salonturbo: turboCoinsEur + turboOwnerTimeEur,
  turboCoinsEur,
  agencyOwnerTimeEur,
  turboOwnerTimeEur,
  oneOffPerMonth,
};

export const includedFeatures = [
  "Kampagnen-Setup für Google-Ads",
  "Werbebudget & Abrechnung mit Google",
  "Eigene Salon-Landingpage mit Anruf-/Termin-Button und Öffnungszeiten",
  "Automatisches Ein- und Ausschalten der Anzeigen",
  "Monitoring der Kampagnen",
  "Automatische Aktualisierung der Anzeige bei Änderungen",
  "Rechnung und Zahlungsübersicht im Dashboard",
  "Optimierung für mobile Endgeräte",
];
